import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Trash2, Grid3X3 } from 'lucide-react';
import { SpotGeneratorDialog } from './SpotGeneratorDialog';

interface ParkingLotSpot {
  id: string;
  label: string;
  spot_type: string;
  geom_px?: number[][][];
  is_active: boolean;
}

interface ParkingLotLayoutEditorProps {
  spots: ParkingLotSpot[];
  onSpotsChange: (spots: ParkingLotSpot[]) => void;
  imageWidth: number;
  imageHeight: number;
  scaleFtPerPixel: number;
}

export function ParkingLotLayoutEditor({
  spots,
  onSpotsChange,
  imageWidth,
  imageHeight,
  scaleFtPerPixel,
}: ParkingLotLayoutEditorProps) {
  const [showGenerator, setShowGenerator] = useState(false);

  const handleDeleteSpot = (spotId: string) => {
    onSpotsChange(spots.filter(s => s.id !== spotId));
  };

  const handleUpdateSpotLabel = (spotId: string, newLabel: string) => {
    onSpotsChange(spots.map(s => s.id === spotId ? { ...s, label: newLabel } : s));
  };

  const handleGenerateSpots = (generatedSpots: Array<{
    label: string;
    spot_type: string;
    geom_px: number[][][];
    is_active: boolean;
  }>) => {
    const newSpots = generatedSpots.map((spot, index) => ({
      id: `temp-${Date.now()}-${index}`,
      ...spot,
    }));
    onSpotsChange([...spots, ...newSpots]);
    setShowGenerator(false);
  };

  return (
    <Card className="w-72 flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Grid3X3 className="h-4 w-4" />
          Layout Editor
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4 p-4 pt-0">
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={() => setShowGenerator(true)}
        >
          <Plus className="h-4 w-4" />
          Generate Row
        </Button>

        <ScrollArea className="flex-1 -mx-4 px-4">
          <div className="space-y-2">
            {spots.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No spots defined. Use "Generate Row" to create parking spots.
              </p>
            ) : (
              spots.map(spot => (
                <div key={spot.id} className="flex items-center gap-2">
                  <Input
                    value={spot.label}
                    onChange={(e) => handleUpdateSpotLabel(spot.id, e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteSpot(spot.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
          <p>Total spots: {spots.length}</p>
          <p>Scale: {scaleFtPerPixel.toFixed(4)} ft/px</p>
        </div>
      </CardContent>

      <SpotGeneratorDialog
        open={showGenerator}
        onOpenChange={setShowGenerator}
        onGenerate={handleGenerateSpots}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        scaleFtPerPixel={scaleFtPerPixel}
        existingLabels={spots.map(s => s.label)}
      />
    </Card>
  );
}
